import { useState } from 'react';
import { evaluateBioWithLovableAI } from '@/utils/lovableAIBioEvaluator';
import type { SimplifiedBioAnalysis } from '@/types/bio-analysis';
import { getBioCategoryFromScore } from '@/utils/bioCategories';
import { ValidationRules, BIO_MIN_LENGTH } from '@/core/BusinessRulesEngine';

/**
 * Simplified Bio Validation Result - v3
 * Focado apenas em tópicos para matching simples
 */
export interface SimplifiedBioValidationResult {
  isWellWritten: boolean;
  topicsMentioned: string[]; // Mesmo que topics
  feedback: string;
  score: number;
  loading?: boolean;
  error?: string;
  
  // Campos de validação
  canSave: boolean;
  canProceed: boolean;
  shouldBlock: boolean;
  category?: ReturnType<typeof getBioCategoryFromScore>;
  
  // Análise simplificada
  simplifiedAnalysis?: SimplifiedBioAnalysis;
}

// Alias para compatibilidade
export type EnhancedBioValidationResult = SimplifiedBioValidationResult;

/**
 * Hook para validação enriquecida de bio usando Lovable AI
 * 
 * Substitui useOnDemandBioValidation com análise 2x mais rica e 5x mais rápida
 */
export const useEnhancedBioValidation = () => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<EnhancedBioValidationResult | null>(null);

  const validateBio = async (bio: string): Promise<EnhancedBioValidationResult> => {
    if (!bio || bio.trim().length < BIO_MIN_LENGTH) {
      const result: EnhancedBioValidationResult = {
        isWellWritten: false,
        topicsMentioned: [],
        feedback: 'Bio muito curta para análise',
        score: 0,
        loading: false,
        canSave: false,
        canProceed: false,
        shouldBlock: true
      };
      setValidationResult(result);
      return result;
    }

    setIsValidating(true);
    
    try {
      const simplifiedAnalysis = await evaluateBioWithLovableAI(bio);
      const category = getBioCategoryFromScore(simplifiedAnalysis.score);
      
      // Use Business Rules Engine to determine save/proceed status
      const canSave = ValidationRules.canSaveBio(simplifiedAnalysis.score, simplifiedAnalysis.isWellWritten);
      const canProceed = ValidationRules.canProceedWithBio(simplifiedAnalysis.score, simplifiedAnalysis.isWellWritten);
      
      const result: EnhancedBioValidationResult = {
        isWellWritten: simplifiedAnalysis.isWellWritten,
        topicsMentioned: simplifiedAnalysis.topics,
        feedback: simplifiedAnalysis.feedback,
        score: simplifiedAnalysis.score,
        loading: false,
        canSave,
        canProceed,
        shouldBlock: !canProceed,
        category,
        simplifiedAnalysis
      };
      
      setValidationResult(result);
      return result;
    } catch (error) {
      console.error('Error in enhanced bio validation:', error);
      const result: EnhancedBioValidationResult = {
        isWellWritten: false,
        topicsMentioned: [],
        feedback: 'Erro ao avaliar bio. Tente novamente.',
        score: 0,
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        canSave: false,
        canProceed: false,
        shouldBlock: true
      };
      setValidationResult(result);
      return result;
    } finally {
      setIsValidating(false);
    }
  };

  const clearValidation = () => {
    setValidationResult(null);
  };

  return {
    isValidating,
    validationResult,
    validateBio,
    clearValidation
  };
};

